import { ScrollPicker, PickerValue } from "@/components/scroll-picker";
import {
  AmendSheetShell,
  ConfirmChangeButton,
} from "@/features/onboarding/components/amend-sheet-shell";
import { useGetCalories } from "@/hooks/requests/nutritional-requirement/use-get-calories";
import { useGetMacros } from "@/hooks/requests/nutritional-requirement/use-get-macros";
import { useUpdateMacros } from "@/hooks/requests/nutritional-requirement/use-update-macros";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { useEffect, useMemo, useRef, useState } from "react";

interface AmendMacrosSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const SHEET_HEIGHT_PX = 566;

type ViewMode = "percentage" | "grams";

const PROTEIN_RANGE = { min: 10, max: 50 };
const FAT_RANGE = { min: 15, max: 50 };
const CARBS_RANGE = { min: 20, max: 60 };

const buildRange = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => String(min + i));

const PROTEIN_OPTIONS = buildRange(PROTEIN_RANGE.min, PROTEIN_RANGE.max);
const CARBS_OPTIONS = buildRange(CARBS_RANGE.min, CARBS_RANGE.max);
const FAT_OPTIONS = buildRange(FAT_RANGE.min, FAT_RANGE.max);

const pctToGrams = (pct: number, calories: number, kcalPerGram: number) =>
  Math.round(((pct / 100) * calories) / kcalPerGram);

const gramsToPct = (grams: number, calories: number, kcalPerGram: number) =>
  Math.round(((grams * kcalPerGram) / calories) * 100);

const renderItem = (value: string, selected: boolean) => (
  <div
    className={cn(
      "flex h-[50px] w-full items-center justify-center text-[22px] font-semibold",
      selected ? "text-[#43246e]" : "text-[#7485a7]",
    )}
  >
    {value}
  </div>
);

interface SegmentedProps {
  value: ViewMode;
  onChange: (value: ViewMode) => void;
}

const Segmented = ({ value, onChange }: SegmentedProps) => (
  <div className="flex h-11 w-full items-center gap-2 rounded-[32px] bg-[#f9f9f9] p-1">
    {(["percentage", "grams"] as const).map((mode) => (
      <button
        key={mode}
        type="button"
        onClick={() => onChange(mode)}
        className={cn(
          "flex h-full flex-1 items-center justify-center rounded-[37px] text-base text-black",
          value === mode
            ? "bg-white font-semibold shadow-[4px_4px_24px_0_rgba(215,214,214,0.15)]"
            : "font-medium text-[#535252]",
        )}
      >
        {mode === "percentage" ? "%" : "Grams"}
      </button>
    ))}
  </div>
);

/**
 * Macros bottom sheet (Figma 983:5865). Ported from the old EditMacrosModal —
 * canonical state in percentages, %/Grams toggle, must-total-100 rule — with
 * the container swapped from a centered modal to the v5 action sheet.
 */
export const AmendMacrosSheet = ({ isOpen, onDismiss }: AmendMacrosSheetProps) => {
  const { showError } = useToast();
  const { calories } = useGetCalories();
  const { macros, isLoading: isMacrosLoading } = useGetMacros();
  const { updateMacrosAsync, isUpdating: isUpdatingMacros } = useUpdateMacros();

  const [viewMode, setViewMode] = useState<ViewMode>("percentage");
  const [protein, setProtein] = useState(30);
  const [carbs, setCarbs] = useState(40);
  const [fats, setFats] = useState(30);
  const seededRef = useRef(false);

  const dailyCalories = calories?.dailyCalories ?? 2000;

  useEffect(() => {
    if (!isOpen) {
      seededRef.current = false;
      return;
    }
    if (macros && !seededRef.current) {
      setProtein(Math.round(macros.proteinPercentage));
      setCarbs(Math.round(macros.carbsPercentage));
      setFats(Math.round(macros.fatPercentage));
      setViewMode("percentage");
      seededRef.current = true;
    }
  }, [isOpen, macros]);

  const proteinGrams = useMemo(
    () => pctToGrams(protein, dailyCalories, 4),
    [protein, dailyCalories],
  );
  const carbsGrams = useMemo(() => pctToGrams(carbs, dailyCalories, 4), [carbs, dailyCalories]);
  const fatsGrams = useMemo(() => pctToGrams(fats, dailyCalories, 9), [fats, dailyCalories]);

  const total = protein + carbs + fats;
  const isValid = total === 100;

  const pctPickerValue: PickerValue = {
    protein: String(protein),
    carbs: String(carbs),
    fats: String(fats),
  };

  const gramsPickerValue: PickerValue = {
    protein: String(proteinGrams),
    carbs: String(carbsGrams),
    fats: String(fatsGrams),
  };

  const handlePctChange = (next: PickerValue) => {
    setProtein(Number(next.protein));
    setCarbs(Number(next.carbs));
    setFats(Number(next.fats));
  };

  const handleGramsChange = (next: PickerValue) => {
    setProtein(gramsToPct(Number(next.protein), dailyCalories, 4));
    setCarbs(gramsToPct(Number(next.carbs), dailyCalories, 4));
    setFats(gramsToPct(Number(next.fats), dailyCalories, 9));
  };

  const gramsProteinOptions = useMemo(
    () =>
      buildRange(
        pctToGrams(PROTEIN_RANGE.min, dailyCalories, 4),
        pctToGrams(PROTEIN_RANGE.max, dailyCalories, 4),
      ),
    [dailyCalories],
  );
  const gramsCarbsOptions = useMemo(
    () =>
      buildRange(
        pctToGrams(CARBS_RANGE.min, dailyCalories, 4),
        pctToGrams(CARBS_RANGE.max, dailyCalories, 4),
      ),
    [dailyCalories],
  );
  const gramsFatsOptions = useMemo(
    () =>
      buildRange(
        pctToGrams(FAT_RANGE.min, dailyCalories, 9),
        pctToGrams(FAT_RANGE.max, dailyCalories, 9),
      ),
    [dailyCalories],
  );

  // Header row shows the OTHER unit's values
  const showingGrams = viewMode === "percentage";
  const headerValues = showingGrams
    ? { protein: `${proteinGrams} g`, carbs: `${carbsGrams} g`, fats: `${fatsGrams} g` }
    : { protein: `${protein}%`, carbs: `${carbs}%`, fats: `${fats}%` };

  const handleConfirm = async () => {
    if (!isValid) {
      showError("Macros must total 100%");
      return;
    }
    try {
      await updateMacrosAsync(
        {
          proteinPercentage: protein,
          fatPercentage: fats,
          carbsPercentage: carbs,
        },
        {
          onSuccess: () => onDismiss(),
          onError: (error) => showError(error.message),
        },
      );
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to update macros");
    }
  };

  return (
    <AmendSheetShell isOpen={isOpen} onDismiss={onDismiss} contentHeightPx={SHEET_HEIGHT_PX}>
      <div className="flex w-full flex-col items-center gap-3">
        <Segmented value={viewMode} onChange={setViewMode} />

        <div className="grid w-full grid-cols-3 gap-2 rounded-[12px] bg-[#f9f9f9] px-3 py-4 text-center">
          <div>
            <p className="text-base font-bold text-black">Protein</p>
            <p className="pt-1 text-base text-black">{headerValues.protein}</p>
          </div>
          <div>
            <p className="text-base font-bold text-black">Carbs</p>
            <p className="pt-1 text-base text-black">{headerValues.carbs}</p>
          </div>
          <div>
            <p className="text-base font-bold text-black">Fats</p>
            <p className="pt-1 text-base text-black">{headerValues.fats}</p>
          </div>
        </div>

        <div className="w-full max-w-[322px]">
          {viewMode === "percentage" ? (
            <ScrollPicker
              value={pctPickerValue}
              onChange={handlePctChange}
              height={150}
              itemHeight={50}
            >
              <ScrollPicker.Column
                name="protein"
                options={PROTEIN_OPTIONS}
                renderItem={renderItem}
              />
              <ScrollPicker.Column name="carbs" options={CARBS_OPTIONS} renderItem={renderItem} />
              <ScrollPicker.Column name="fats" options={FAT_OPTIONS} renderItem={renderItem} />
            </ScrollPicker>
          ) : (
            <ScrollPicker
              value={gramsPickerValue}
              onChange={handleGramsChange}
              height={150}
              itemHeight={50}
            >
              <ScrollPicker.Column
                name="protein"
                options={gramsProteinOptions}
                renderItem={renderItem}
              />
              <ScrollPicker.Column
                name="carbs"
                options={gramsCarbsOptions}
                renderItem={renderItem}
              />
              <ScrollPicker.Column name="fats" options={gramsFatsOptions} renderItem={renderItem} />
            </ScrollPicker>
          )}
        </div>

        <div className="flex w-full items-center justify-between border-t border-[#f5f5f5] pt-5">
          <p className="text-base font-medium text-black">Total: {total}%</p>
          <p
            className={cn("text-[13px] font-medium", isValid ? "text-[#717171]" : "text-[#ff008c]")}
          >
            Macros must equal 100%
          </p>
        </div>

        <ConfirmChangeButton
          onClick={handleConfirm}
          disabled={!isValid || isMacrosLoading}
          isSaving={isUpdatingMacros}
        />
      </div>
    </AmendSheetShell>
  );
};
